import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTrialStatus } from '../hooks/useTrialStatus';

const SubscriptionPopup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { hasTrial, isExpired, daysRemaining, loading } = useTrialStatus();

    const [showWarning, setShowWarning] = useState(false);
    const [showExpiredModal, setShowExpiredModal] = useState(false);

    // List of allowed routes after trial expiry
    const allowedRoutesAfterExpiry = [
        '/dashboard',
        '/reports',
        '/settings',
        '/subscription',
        '/payment-success'
    ];

    useEffect(() => {
        if (loading) return;

        if (hasTrial && !isExpired && daysRemaining <= 1 && daysRemaining > 0) {
            setShowWarning(true);
        }

        if (isExpired) {
            const currentPath = location.pathname;
            const isAllowedRoute = allowedRoutesAfterExpiry.some(route =>
                currentPath.startsWith(route)
            );

            if (!isAllowedRoute) {
                setShowExpiredModal(true);
            }
        }
    }, [hasTrial, isExpired, daysRemaining, loading, location.pathname]);

    const handleUpgrade = () => {
        setShowWarning(false);
        setShowExpiredModal(false);
        navigate('/subscription');
    };

    const handleContinue = () => {
        setShowWarning(false);
    };

    const handleGoToDashboard = () => {
        setShowExpiredModal(false);
        navigate('/dashboard');
    };

    // Inline Styles
    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.3s ease-out',
        },
        modal: {
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            animation: 'slideUp 0.4s ease-out',
        },
        modalFullscreen: {
            maxWidth: '100%',
            maxHeight: '100vh',
            height: '100%',
            borderRadius: '0',
        },
        header: {
            background: '#d63384',
            padding: '32px',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            position: 'relative',
            flexShrink: 0,
        },
        headerWarning: {
            background: '#e91e63',
        },
        headerExpired: {
            background: '#b02a5b',
        },
        closeButton: {
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: '#ffffff',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
        },
        headerTitle: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#ffffff',
            margin: 0,
            textAlign: 'center',
            letterSpacing: '-0.5px',
        },
        headerSubtitle: {
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginTop: '8px',
            textAlign: 'center',
            fontWeight: '400',
        },
        body: {
            padding: '30px 32px',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
        },
        countdownSection: {
            textAlign: 'center',
            marginBottom: '24px',
        },
        countdownCircle: {
            width: '180px',
            height: '180px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: '#d63384',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 60px rgba(214, 51, 132, 0.4)',
            position: 'relative',
            overflow: 'hidden',
        },
        countdownCircleWarning: {
            background: '#e91e63',
            boxShadow: '0 20px 60px rgba(233, 30, 99, 0.4)',
        },
        countdownNumber: {
            fontSize: '72px',
            fontWeight: '800',
            color: '#ffffff',
            lineHeight: '1',
            marginBottom: '8px',
        },
        countdownLabel: {
            fontSize: '18px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        lockIcon: {
            fontSize: '120px',
            marginBottom: '24px',
        },
        contentTitle: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#2d2d2d',
            marginBottom: '16px',
            textAlign: 'center',
            lineHeight: '1.3',
        },
        contentText: {
            fontSize: '16px',
            color: '#6c757d',
            marginBottom: '24px',
            textAlign: 'center',
            lineHeight: '1.6',
        },
        featureBox: {
            background: '#fafafa',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #f0f0f0',
        },
        featureBoxSuccess: {
            background: '#fce4ec',
            border: '2px solid #f8bbd9',
        },
        featureBoxDanger: {
            background: '#fff5f5',
            border: '2px solid #fed7d7',
        },
        featureTitle: {
            fontSize: '16px',
            fontWeight: '700',
            color: '#2d2d2d',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        featureList: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: '#ffffff',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#2d2d2d',
            transition: 'all 0.3s ease',
            border: '1px solid #f0f0f0',
        },
        featureItemSuccess: {
            borderLeft: '4px solid #d63384',
        },
        featureItemDanger: {
            borderLeft: '4px solid #e91e63',
        },
        icon: {
            fontSize: '20px',
            flexShrink: 0,
        },
        iconSuccess: {
            color: '#d63384',
        },
        iconDanger: {
            color: '#e91e63',
        },
        iconWarning: {
            color: '#d63384',
        },
        footer: {
            padding: '24px 32px 32px',
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            flexShrink: 0,
            borderTop: '1px solid #f0f0f0',
        },
        button: {
            padding: '16px 32px',
            fontSize: '16px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            minWidth: '160px',
            textAlign: 'center',
        },
        buttonPrimary: {
            background: '#d63384',
            color: '#ffffff',
            boxShadow: '0 2px 12px rgba(214, 51, 132, 0.3)',
        },
        buttonSuccess: {
            background: '#d63384',
            color: '#ffffff',
            boxShadow: '0 2px 12px rgba(214, 51, 132, 0.3)',
        },
        buttonSecondary: {
            background: '#ffffff',
            color: '#2d2d2d',
            border: '2px solid #f0f0f0',
        },
        divider: {
            height: '1px',
            background: '#f0f0f0',
            margin: '24px 0',
        },
        badge: {
            display: 'inline-block',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            marginBottom: '16px',
        },
        badgeWarning: {
            background: '#fce4ec',
            color: '#b02a5b',
        },
        badgeDanger: {
            background: '#fce4ec',
            color: '#b02a5b',
        },
    };

    // Global styles injected as a style tag
    const globalStyles = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes slideUp {
            from { 
                opacity: 0;
                transform: translateY(50px);
            }
            to { 
                opacity: 1;
                transform: translateY(0);
            }
        }
        @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
        }
        
        /* Custom Scrollbar for body */
        .body-responsive::-webkit-scrollbar {
            width: 6px;
        }
        .body-responsive::-webkit-scrollbar-track {
            background: #fafafa;
            border-radius: 10px;
        }
        .body-responsive::-webkit-scrollbar-thumb {
            background: #d63384;
            border-radius: 10px;
        }
        .body-responsive::-webkit-scrollbar-thumb:hover {
            background: #b02a5b;
        }
        
        .close-btn:hover {
            background: rgba(255, 255, 255, 0.3) !important;
            transform: rotate(90deg);
        }
        .feature-item-hover:hover {
            transform: translateX(4px);
            box-shadow: 0 2px 12px rgba(214, 51, 132, 0.15);
        }
        .button-hover:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(214, 51, 132, 0.25);
            background: #b02a5b;
        }
        .button-secondary-hover:hover {
            background: #fafafa;
            border-color: #d63384;
        }
        @media (max-width: 768px) {
            .modal-responsive {
                border-radius: 0 !important;
                max-width: 100% !important;
                max-height: 100vh !important;
                height: 100% !important;
                display: flex !important;
                flex-direction: column !important;
            }
            .header-responsive {
                border-radius: 0 !important;
                padding: 20px !important;
                flex-shrink: 0 !important;
            }
            .body-responsive {
                padding: 20px !important;
                flex: 1 !important;
                overflow-y: auto !important;
            }
            .footer-responsive {
                padding: 16px 20px !important;
                flex-direction: column;
                flex-shrink: 0 !important;
            }
            .button-responsive {
                width: 100%;
                min-width: auto !important;
            }
            .countdown-responsive {
                width: 120px !important;
                height: 120px !important;
            }
            .countdown-number-responsive {
                font-size: 48px !important;
            }
            .title-responsive {
                font-size: 22px !important;
            }
            .content-title-responsive {
                font-size: 20px !important;
            }
        }
    `;

    // Warning Modal (1 day left)
    if (showWarning && !isExpired) {
        return (
            <>
                <style>{globalStyles}</style>
                <div style={styles.overlay} onClick={handleContinue}>
                    <div
                        className="modal-responsive"
                        style={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="header-responsive" style={{ ...styles.header, ...styles.headerWarning }}>
                            <button
                                className="close-btn"
                                style={styles.closeButton}
                                onClick={handleContinue}
                            >
                                ×
                            </button>
                            <h1 className="title-responsive" style={styles.headerTitle}>
                                ⏰ Trial Ending Soon
                            </h1>
                            <p style={styles.headerSubtitle}>
                                Don't lose access to premium features
                            </p>
                        </div>

                        <div className="body-responsive" style={styles.body}>
                            <div style={styles.countdownSection}>
                                <div
                                    className="countdown-responsive"
                                    style={{ ...styles.countdownCircle, ...styles.countdownCircleWarning }}
                                >
                                    <div className="countdown-number-responsive" style={styles.countdownNumber}>
                                        {daysRemaining}
                                    </div>
                                    <div style={styles.countdownLabel}>
                                        {daysRemaining === 1 ? 'Day Left' : 'Days Left'}
                                    </div>
                                </div>
                            </div>

                            <h2 className="content-title-responsive" style={styles.contentTitle}>
                                Your Free Trial Expires Soon
                            </h2>
                            <p style={styles.contentText}>
                                Upgrade now to continue enjoying all premium features without interruption.
                                Don't let your progress stop!
                            </p>

                            <div style={styles.featureBox}>
                                <div style={styles.featureTitle}>
                                    <span style={styles.iconWarning}>⚠️</span>
                                    What Happens After Trial Ends?
                                </div>
                                <div style={styles.featureList}>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>Dashboard Access</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>View Reports</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Premium Features</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Advanced Tools</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="footer-responsive" style={styles.footer}>
                            <button
                                className="button-responsive button-secondary-hover"
                                style={{ ...styles.button, ...styles.buttonSecondary }}
                                onClick={handleContinue}
                            >
                                Continue Trial
                            </button>
                            <button
                                className="button-responsive button-hover"
                                style={{ ...styles.button, ...styles.buttonPrimary }}
                                onClick={handleUpgrade}
                            >
                                Upgrade Now
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Expired Modal
    if (showExpiredModal && isExpired) {
        return (
            <>
                <style>{globalStyles}</style>
                <div style={styles.overlay}>
                    <div
                        className="modal-responsive"
                        style={styles.modal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="header-responsive" style={{ ...styles.header, ...styles.headerExpired }}>
                            <h1 className="title-responsive" style={styles.headerTitle}>
                                🔒 Trial Expired
                            </h1>
                            <p style={styles.headerSubtitle}>
                                Upgrade to continue using premium features
                            </p>
                        </div>

                        <div className="body-responsive" style={styles.body}>
                            <div style={styles.countdownSection}>
                                <div style={styles.lockIcon}>🔐</div>
                            </div>

                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                <span style={{ ...styles.badge, ...styles.badgeDanger }}>
                                    7-Day Free Trial Ended
                                </span>
                            </div>

                            <h2 className="content-title-responsive" style={styles.contentTitle}>
                                Your Free Trial Has Ended
                            </h2>
                            <p style={styles.contentText}>
                                To continue using premium features like Invoices, Customers, Inventory,
                                and Transactions, please upgrade to a paid plan.
                            </p>

                            <div style={{ ...styles.featureBox, ...styles.featureBoxSuccess }}>
                                <div style={styles.featureTitle}>
                                    <span style={styles.iconSuccess}>✓</span>
                                    Still Available to You
                                </div>
                                <div style={styles.featureList}>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>Dashboard (View Only)</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>Reports</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>Profile Settings</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemSuccess }}>
                                        <span style={{ ...styles.icon, ...styles.iconSuccess }}>✓</span>
                                        <span>Basic Features</span>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.divider}></div>

                            <div style={{ ...styles.featureBox, ...styles.featureBoxDanger }}>
                                <div style={styles.featureTitle}>
                                    <span style={styles.iconDanger}>🔒</span>
                                    Locked Premium Features
                                </div>
                                <div style={styles.featureList}>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Invoice Management</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Customer Database</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Inventory Tracking</span>
                                    </div>
                                    <div className="feature-item-hover" style={{ ...styles.featureItem, ...styles.featureItemDanger }}>
                                        <span style={{ ...styles.icon, ...styles.iconDanger }}>🔒</span>
                                        <span>Transaction History</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="footer-responsive" style={styles.footer}>
                            <button
                                className="button-responsive button-secondary-hover"
                                style={{ ...styles.button, ...styles.buttonSecondary }}
                                onClick={handleGoToDashboard}
                            >
                                Go to Dashboard
                            </button>
                            <button
                                className="button-responsive button-hover"
                                style={{ ...styles.button, ...styles.buttonSuccess }}
                                onClick={handleUpgrade}
                            >
                                Upgrade Now
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return null;
};

export default SubscriptionPopup;